import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from '../../auth/staffAuthApi';
import { mainAuth as auth } from '../../auth/appAuth';
import { VerifyEmailModal } from '../../auth/verify-email/VerifyEmailModal';
import { useAuth } from '../../context/AuthContext';
import TwoFactorSmsModal from '../../features/two-factor-auth/components/TwoFactorSmsModal';
import { isPendingSecondFactorActive, clearPendingSecondFactor, readPendingMethod, clearSecondFactorVerifiedSession, isSecondFactorVerifiedSession } from '../../features/two-factor-auth/utils/pendingSecondFactorSession';
import {
  IconEmail,
  IconLock,
  IconEye,
  IconEyeOff,
  IconCalendar,
  IconUsers,
  IconGrowth,
  IconInstall,
} from './login/components/LoginIcons';
import { formatAuthError } from '../../auth/utils';
import { clearLocalSessionToken } from '../../api/authToken';
import { setPreference, getPreference, PREFERENCE_KEYS } from '../../auth/preferences/userPreferences';
import { useNotification } from '../../context/NotificationContext';
import NeatScheduleMark from '../../shared/components/brand/NeatScheduleMark';
import WorkspaceLoadingScreen from '../loading/workspace/WorkspaceLoadingScreen';
import { isImpersonationEntering, isImpersonationExiting, isImpersonationSessionActive, clearImpersonationExiting, clearImpersonationEntering, getSignedImpersonationSession } from '../../impersonation/utils/impersonationSession';
import { hasPendingImpersonationHandoff } from '../../impersonation/utils/impersonationHandoff';
import './login/styles/LoginPage.css';

const BRAND_FEATURES = [
  {
    icon: IconCalendar,
    title: 'Smart scheduling',
    text: 'Manage appointments, availability, and bookings in one place.',
  },
  {
    icon: IconUsers,
    title: 'Team & clients',
    text: 'Keep your staff and client relationships organized effortlessly.',
  },
  {
    icon: IconGrowth,
    title: 'Built for growth',
    text: 'Professional tools that scale with your business.',
  },
];

const Login = () => {
  // Pin theme for this page so company theme load after password sign-in cannot repaint login.
  const loginDisplayMode = useMemo(
    () => localStorage.getItem('themeMode') || 'light',
    []
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { showToast } = useNotification();
  const navigate = useNavigate();
  const {
    user,
    loading: authLoading,
    signInWithSecondFactor,
    resumeSecondFactorForCurrentUser,
    verifyPendingSmsCode,
    getPendingSecondFactorInfo,
  } = useAuth();
  const [loginPhase, setLoginPhase] = useState(() => {
    if (!isPendingSecondFactorActive()) return 'credentials';
    const method = readPendingMethod();
    if (method === 'sms') return 'sms-verify';
    if (method === 'email') return 'email-wait';
    return 'credentials';
  });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const location = useLocation();
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [accountNotFound, setAccountNotFound] = useState(false);
  const [secondFactorSent, setSecondFactorSent] = useState(false);
  const [secondFactorSending, setSecondFactorSending] = useState(false);
  const [secondFactorMethod, setSecondFactorMethod] = useState('');
  const [maskedDestination, setMaskedDestination] = useState('');
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsCode, setSmsCode] = useState('');
  const [smsError, setSmsError] = useState('');
  const lastAutoSubmittedCodeRef = useRef('');
  const resumeAttemptedRef = useRef(false);
  const hadVerifiedSessionOnMountRef = useRef(null);

  const applyRememberMePreference = (shouldRemember) => {
    if (shouldRemember) {
      localStorage.setItem('rememberMe', 'true');
      localStorage.setItem('mainAppRememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('mainAppRememberMe');
    }
  };

  const resolveRememberMePreference = () =>
    rememberMe ||
    localStorage.getItem('mainAppRememberMe') === 'true' ||
    localStorage.getItem('websiteRememberMe') === 'true';

  const handleSignInResult = (signInResult) => {
    if (signInResult?.emailVerificationRequired) {
      setShowVerificationModal(true);
      return true;
    }

    if (signInResult?.secondFactorSending) {
      const method = signInResult?.secondFactorMethod === 'sms' ? 'sms' : 'email';
      setSecondFactorMethod(method);
      setSecondFactorSending(true);
      setSecondFactorSent(false);
      setMaskedDestination('');
      if (method === 'sms') {
        setLoginPhase('sms-verify');
        setShowSmsModal(true);
      } else {
        setLoginPhase('email-wait');
      }
      return true;
    }

    if (signInResult?.secondFactorSent) {
      applyRememberMePreference(resolveRememberMePreference());
      const method = signInResult?.secondFactorMethod === 'sms' ? 'sms' : 'email';
      setSecondFactorMethod(method);
      setMaskedDestination(signInResult?.maskedDestination || '');
      setSecondFactorSending(false);
      setSecondFactorSent(true);
      if (method === 'sms') {
        setLoginPhase('sms-verify');
        setShowSmsModal(true);
      } else {
        setLoginPhase('email-wait');
      }
      return true;
    }

    if (signInResult?.passwordOnly) {
      applyRememberMePreference(resolveRememberMePreference());
      sessionStorage.removeItem('accountSetupInProgress');
      localStorage.setItem('mainAppLoginTimestamp', Date.now().toString());
      localStorage.setItem('mainAppLastSessionRefresh', Date.now().toString());
      localStorage.setItem('lastSessionRefresh', Date.now().toString());
      navigate('/app/overview', { replace: true });
      return true;
    }

    return false;
  };

  const completeSecondFactorSend = async (signInResult) => {
    if (!signInResult?.completeSecondFactorSend) return;
    setSecondFactorSending(true);
    try {
      const completed = await signInResult.completeSecondFactorSend();
      handleSignInResult(completed);
    } catch (signInError) {
      setShowSmsModal(false);
      handleSignInError(signInError);
    } finally {
      setSecondFactorSending(false);
    }
  };

  const handleSignInError = (signInError) => {
    setSecondFactorSent(false);
    setSecondFactorSending(false);
    setLoginPhase('credentials');
    if (signInError.code === 'auth/multi-factor-auth-required') {
      setError('This account uses an old sign-in code. Contact support to move it to the current sign-in settings.');
      return;
    }

    let errorMessage;
    switch (signInError.code) {
      case 'auth/too-many-requests': {
        const currentTime = Date.now();
        const waitTime = rateLimitEndTime
          ? Math.max(30000, (rateLimitEndTime - currentTime) * 2)
          : 30000;
        const newEndTime = currentTime + waitTime;
        setRateLimitEndTime(newEndTime);
        const initialMinutes = Math.floor(waitTime / 60000);
        const initialSeconds = Math.floor((waitTime % 60000) / 1000);
        setCountdown({ minutes: initialMinutes, seconds: initialSeconds });
        errorMessage = 'Too many sign-in attempts. Please wait a moment and try again.';
        break;
      }
      case 'auth/wrong-password':
      case 'auth/invalid-credential':
        errorMessage = 'Invalid email or password.';
        break;
      case 'auth/user-not-found':
        errorMessage = 'No account exists with this email address.';
        break;
      default:
        errorMessage = signInError?.message || 'An error occurred during login. Please try again.';
    }

    setError(errorMessage);
  };

  // Resend verification email countdown (shown at bottom of login when cooldown active)
  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const t = setInterval(() => setResendCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldownSeconds]);

  // Add this near the top with other state variables
  const [rateLimitEndTime, setRateLimitEndTime] = useState(null);

  // Add these state variables
  const [countdown, setCountdown] = useState({ minutes: 0, seconds: 0 });

  // Restore 2FA UI only on mount (e.g. page refresh mid-flow). Do not react to
  // pendingSecondFactorLogin during sign-in — that fires before the code is sent
  // and causes alert/modal flicker.
  useEffect(() => {
    if (!isPendingSecondFactorActive()) return;
    const pending = getPendingSecondFactorInfo();
    const method =
      pending?.secondFactorMethod === 'sms'
        ? 'sms'
        : pending?.secondFactorMethod === 'email'
          ? 'email'
          : '';
    if (!method) return;
    setSecondFactorMethod(method);
    setMaskedDestination(pending?.maskedDestination || '');
    setSecondFactorSent(true);
    if (method === 'sms') {
      setLoginPhase('sms-verify');
      setShowSmsModal(true);
    } else if (method === 'email') {
      setLoginPhase('email-wait');
    }
  }, [getPendingSecondFactorInfo]);

  useEffect(() => {
    const loadRememberMePreference = async () => {
      const savedPreference = await getPreference(PREFERENCE_KEYS.REMEMBER_ME);
      if (savedPreference !== null) {
        setRememberMe(savedPreference);
      }
    };
    
    loadRememberMePreference();
    
    if (location.state?.verificationMessage) {
      setError(location.state.verificationMessage);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Don't prevent default - this allows the browser to show the prompt when appropriate
      // e.preventDefault(); - Remove this line
      
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('Install prompt is available');
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setIsInstallable(false);
      // Optional: Track successful installation
      console.log('PWA was installed');
    };

    // Add event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      // Remove event listeners
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (hadVerifiedSessionOnMountRef.current !== null) return;
    hadVerifiedSessionOnMountRef.current = Boolean(
      user?.uid && isSecondFactorVerifiedSession(user.uid)
    );
  }, [authLoading, user]);

  useEffect(() => {
    if (!user || authLoading) return;
    if (hadVerifiedSessionOnMountRef.current === null) return;
    if (isPendingSecondFactorActive()) return;
    if (!isSecondFactorVerifiedSession(user.uid)) return;
    if (sessionStorage.getItem('accountSetupInProgress') === 'true') return;

    const openedFromWebsite = location.search.includes('from=website');
    const signedInThisVisit = hadVerifiedSessionOnMountRef.current === false;
    const impersonating = isImpersonationSessionActive();
    if (!openedFromWebsite && !signedInThisVisit && !impersonating) return;

    localStorage.setItem('mainAppLoginTimestamp', Date.now().toString());
    localStorage.setItem('mainAppLastSessionRefresh', Date.now().toString());
    localStorage.setItem('lastSessionRefresh', Date.now().toString());
    navigate('/app/overview', { replace: true });
    try {
      window.dispatchEvent(new CustomEvent('app-theme-refresh'));
    } catch {
      // ignore
    }
  }, [navigate, user, authLoading, location.search]);

  // Resume app 2FA when a session exists but app verification is not complete
  // (e.g. owner signed in on the website, then opened the app).
  useEffect(() => {
    if (authLoading) return;

    if (!auth.currentUser) {
      resumeAttemptedRef.current = false;
      return;
    }

    const uid = auth.currentUser.uid;
    if (isSecondFactorVerifiedSession(uid)) return;
    if (isPendingSecondFactorActive()) return;
    if (resumeAttemptedRef.current) return;

    const shouldResumeFromWebsite =
      location.search.includes('from=website') ||
      location.state?.resumeAppSecondFactor === true;

    if (!shouldResumeFromWebsite) return;

    resumeAttemptedRef.current = true;

    const resumeEmail = auth.currentUser.email || '';
    if (resumeEmail) {
      setEmail(resumeEmail);
    }

    (async () => {
      setLoading(true);
      setError('');
      setSecondFactorSent(false);
      setMaskedDestination('');
      setSmsCode('');
      setShowSmsModal(false);
      setSmsError('');
      lastAutoSubmittedCodeRef.current = '';

      try {
        const signInResult = await resumeSecondFactorForCurrentUser();
        const handled = handleSignInResult(signInResult);
        if (handled && signInResult?.secondFactorSending) {
          setLoading(false);
          await completeSecondFactorSend(signInResult);
          return;
        }
      } catch (signInError) {
        handleSignInError(signInError);
      } finally {
        setLoading(false);
      }
    })();
  }, [authLoading, location.search, location.state, resumeSecondFactorForCurrentUser]);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      console.log('No installation prompt available');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setIsInstallable(false);

    // Optionally, send analytics event
    console.log(`User response to the install prompt: ${outcome}`);
  };

  const handleNewNotification = (message, severity = 'error') => {
    showToast(message, severity);
  };

  const handleResendVerification = async () => {
    // Instead of handling verification directly, show the modal
    setShowVerificationModal(true);
  };

  const handleAuthError = formatAuthError;

  // Update the checkRateLimit function to include countdown
  const checkRateLimit = () => {
    if (rateLimitEndTime) {
      const now = Date.now();
      if (now >= rateLimitEndTime) {
        setRateLimitEndTime(null);
        setError('');
        setCountdown({ minutes: 0, seconds: 0 });
      } else {
        const timeLeft = rateLimitEndTime - now;
        const minutes = Math.floor(timeLeft / 60000);
        const seconds = Math.floor((timeLeft % 60000) / 1000);
        setCountdown({ minutes, seconds });
      }
    }
  };

  // Add this effect to check rate limit status
  useEffect(() => {
    if (rateLimitEndTime) {
      const interval = setInterval(checkRateLimit, 1000);
      return () => clearInterval(interval);
    }
  }, [rateLimitEndTime]);

  const resetLoginFlow = async () => {
    try {
      clearLocalSessionToken();
      await signOut(auth);
    } catch {
      // ignore
    }
    clearPendingSecondFactor();
    clearSecondFactorVerifiedSession();
    setLoginPhase('credentials');
    setSecondFactorSent(false);
    setSecondFactorSending(false);
    setSecondFactorMethod('');
    setMaskedDestination('');
    setShowSmsModal(false);
    setSmsCode('');
    setSmsError('');
    lastAutoSubmittedCodeRef.current = '';
  };

  const handleSmsCancel = () => {
    void resetLoginFlow();
  };

  const submitSmsCode = async (codeValue) => {
    setLoading(true);
    setSmsError('');
    try {
      await verifyPendingSmsCode(codeValue);
      setLoginPhase('credentials');
      setShowSmsModal(false);
      if (rememberMe) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('mainAppRememberMe', 'true');
      }
    } catch (err) {
      setSmsError(formatAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSmsVerify = async (e) => {
    e.preventDefault();
    await submitSmsCode(smsCode);
  };

  useEffect(() => {
    if (!showSmsModal || secondFactorMethod !== 'sms' || loading) return;
    if (!/^\d{6}$/.test(smsCode)) return;
    if (lastAutoSubmittedCodeRef.current === smsCode) return;
    lastAutoSubmittedCodeRef.current = smsCode;
    void submitSmsCode(smsCode);
  }, [smsCode, showSmsModal, secondFactorMethod, loading]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();

    if (rateLimitEndTime) {
      const timeLeft = Math.ceil((rateLimitEndTime - Date.now()) / 60000);
      setError(`Please wait ${timeLeft} ${timeLeft === 1 ? 'minute' : 'minutes'} before trying again.`);
      return;
    }

    if (loginPhase === 'sms-verify' || loginPhase === 'email-wait' || isPendingSecondFactorActive()) {
      if (loginPhase === 'sms-verify') {
        setShowSmsModal(true);
      }
      return;
    }

    setLoading(true);
    setError('');
    setSecondFactorSent(false);
    setMaskedDestination('');
    setSmsCode('');
    setShowSmsModal(false);
    setSmsError('');
    lastAutoSubmittedCodeRef.current = '';

    try {
      const signInResult = await signInWithSecondFactor(email, password);
      const handled = handleSignInResult(signInResult);
      if (handled && signInResult?.secondFactorSending) {
        setLoading(false);
        await completeSecondFactorSend(signInResult);
        return;
      }
    } catch (signInError) {
      handleSignInError(signInError);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      clearLocalSessionToken();
      await signOut(auth);
      clearSecondFactorVerifiedSession();
      // Clear only main app storage items
      localStorage.removeItem('mainAppRememberMe');
      localStorage.removeItem('mainAppSessionDuration');
      localStorage.removeItem('mainAppLoginTimestamp');
      localStorage.removeItem('mainAppLastSessionRefresh');
      navigate('/app/login');
    } catch (error) {
      console.error("Error signing out:", error);
      handleNewNotification("Error signing out");
    }
  };

  const handleRememberMeChange = async (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    // Store the preference in IndexedDB
    await setPreference(PREFERENCE_KEYS.REMEMBER_ME, checked);
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Drop a leftover impersonation-exit flag before deciding what to show.
  // That flag is written on this site, then the browser leaves for the admin
  // host, so it stays true and the next Log in click only says it is
  // returning to the admin panel.
  useState(() => {
    if (typeof window === 'undefined') return null;
    if (hasPendingImpersonationHandoff()) return null;
    if (isImpersonationExiting()) clearImpersonationExiting();
    if (isImpersonationEntering() && !getSignedImpersonationSession()) {
      clearImpersonationEntering();
    }
    return null;
  });

  if (isImpersonationEntering() || isImpersonationExiting() || (isImpersonationSessionActive() && (authLoading || user))) {
    return (
      <WorkspaceLoadingScreen
        message={isImpersonationExiting() ? 'Returning to admin panel...' : 'Signing into this account...'}
        detail="Secure admin access · skipping customer 2FA"
      />
    );
  }

  return (
    <div className="loginPage" data-theme={loginDisplayMode}>
      <VerifyEmailModal
        open={showVerificationModal}
        email={email}
        password={password}
        onClose={() => setShowVerificationModal(false)}
        onNotification={handleNewNotification}
        onCooldownStart={(seconds) => setResendCooldownSeconds(seconds)}
        formatError={formatAuthError}
      />

      <aside className="loginPage__brand" aria-label="Neat Schedule">
        <div className="loginPage__brandOverlay" aria-hidden="true" />
        <div className="loginPage__brandInner">
          <NeatScheduleMark variant="dark" className="loginPage__logo" />
          <div>
            <h1 className="loginPage__headline">Run your business with confidence</h1>
            <p className="loginPage__subhead">
              Neat Schedule helps teams manage appointments, clients, and daily operations from one professional workspace.
            </p>
          </div>
          <ul className="loginPage__features">
            {BRAND_FEATURES.map(({ icon: Icon, title, text }) => (
              <li key={title} className="loginPage__feature">
                <span className="loginPage__featureIcon" aria-hidden="true">
                  <Icon />
                </span>
                <div>
                  <p className="loginPage__featureTitle">{title}</p>
                  <p className="loginPage__featureText">{text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <main className="loginPage__formPanel">
        <div className="loginPage__formWrap">
          <header className="loginPage__formHeader">
            <h2 className="loginPage__formTitle">
              {loginPhase === 'sms-verify'
                ? 'Enter verification code'
                : loginPhase === 'email-wait'
                  ? 'Check your email'
                  : 'Welcome back'}
            </h2>
            <p className="loginPage__formSubtitle">
              {loginPhase === 'sms-verify'
                ? 'Sign-in is paused until you enter the SMS code we just sent.'
                : loginPhase === 'email-wait'
                  ? 'Click the link in your email to finish signing in. Keep this page open.'
                  : 'Sign in to continue to your dashboard.'}
            </p>
          </header>

          <div className="loginPage__card">
            {rateLimitEndTime ? (
              <div className="loginPage__rateLimit" role="alert">
                <p className="loginPage__rateLimitTitle">Too many attempts. Please wait:</p>
                <div className="loginPage__rateLimitTimer">
                  {String(countdown.minutes).padStart(2, '0')}:{String(countdown.seconds).padStart(2, '0')}
                </div>
              </div>
            ) : null}

            {error && !rateLimitEndTime && !loading ? (
              <div className="loginPage__alert loginPage__alert--error" role="alert">
                {error}
              </div>
            ) : null}

            {verificationMessage ? (
              <div className="loginPage__alert loginPage__alert--info" role="status">
                {verificationMessage}
              </div>
            ) : null}

            {!error && (secondFactorSending || secondFactorSent) && secondFactorMethod && loginPhase !== 'sms-verify' ? (
              <div className="loginPage__alert loginPage__alert--info" role="status">
                {secondFactorMethod === 'sms'
                  ? secondFactorSending
                    ? 'Sending your SMS code…'
                    : `SMS code sent${maskedDestination ? ` to ${maskedDestination}` : ''}.`
                  : secondFactorSending
                    ? 'Sending your login link…'
                    : 'Login link sent. Check your email and click the link to finish signing in. You can keep this page open — it will log you in automatically.'}
              </div>
            ) : null}

            <form onSubmit={handleEmailLogin}>
              <div className="loginPage__field">
                <label className="loginPage__fieldLabel" htmlFor="email">
                  Email address
                </label>
                <div className="loginPage__inputWrap">
                  <span className="loginPage__inputIcon" aria-hidden="true">
                    <IconEmail />
                  </span>
                  <input
                    id="email"
                    className="loginPage__input"
                    type="text"
                    name="email"
                    autoComplete="username"
                    autoFocus
                    required
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loginPhase !== 'credentials' || loading}
                  />
                </div>
              </div>

              <div className="loginPage__field">
                <label className="loginPage__fieldLabel" htmlFor="password">
                  Password
                </label>
                <div className="loginPage__inputWrap">
                  <span className="loginPage__inputIcon" aria-hidden="true">
                    <IconLock />
                  </span>
                  <input
                    id="password"
                    className="loginPage__input"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    required
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loginPhase !== 'credentials' || loading}
                  />
                  <button
                    type="button"
                    className="loginPage__inputToggle"
                    onClick={handleTogglePasswordVisibility}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <IconEyeOff /> : <IconEye />}
                  </button>
                </div>
              </div>

              <div className="loginPage__row">
                <label className="loginPage__remember">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  className="loginPage__linkBtn"
                  onClick={() => window.open('/app/forgot-password', '_blank')}
                >
                  Forgot password?
                </button>
              </div>

              <button
                type="submit"
                className="loginPage__submit"
                disabled={loading || loginPhase === 'sms-verify' || loginPhase === 'email-wait'}
              >
                {loading
                  ? 'Sending verification…'
                  : loginPhase === 'email-wait'
                    ? 'Waiting for email link…'
                    : loginPhase === 'sms-verify'
                      ? 'Enter SMS code above'
                      : 'Sign in'}
              </button>

              {isInstallable ? (
                <>
                  <div className="loginPage__divider">App</div>
                  <button type="button" className="loginPage__secondaryBtn loginPage__secondaryBtn--install" onClick={handleInstall}>
                    <IconInstall />
                    Install Neat Schedule
                  </button>
                </>
              ) : null}

              {resendCooldownSeconds > 0 ? (
                <div className="loginPage__cooldown">
                  You can send another verification email in
                  <span className="loginPage__cooldownTime">
                    {Math.floor(resendCooldownSeconds / 60)}:{String(resendCooldownSeconds % 60).padStart(2, '0')}
                  </span>
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </main>

      <TwoFactorSmsModal
        open={showSmsModal && secondFactorMethod === 'sms' && loginPhase === 'sms-verify'}
        blocking
        maskedDestination={maskedDestination}
        sending={secondFactorSending}
        smsCode={smsCode}
        onSmsCodeChange={setSmsCode}
        onSubmit={handleSmsVerify}
        onClose={handleSmsCancel}
        onCancel={handleSmsCancel}
        loading={loading}
        error={smsError}
      />
    </div>
  );
};

export default Login;